// #4401: ask the browser's storage a question it cannot refuse, and see whether it
// answers at all.
//
// When a boot stalls because the device's own store stopped answering, there are two
// very different worlds it could be in, and they need opposite remedies:
//
//  - ONLY THIS DATABASE is jammed. Then opening the store under a different NAME
//    sidesteps the stuck request entirely, and the user can be rescued in place.
//  - THE WHOLE ORIGIN'S STORAGE is jammed. Then a different name is queued behind the
//    same wedge and rescues nobody; only restarting the app, or the browser, can help.
//
// Nothing in the reports could tell those apart, so this opens a THROWAWAY database —
// one that holds nothing and that we are free to abandon — and records whether it
// answers. That one fact decides which rescue is worth offering, and it is cheap enough
// to run every time a boot is reported stuck.
//
// It also asks how much storage space is left, because "free up some space" is advice we
// should only ever give when it is actually true.
//
// Keep this module free of Angular imports: the unit test loads it under plain node and
// supplies its own fakes. Same pattern as local-op-tracker.ts and local-store-timeout.ts.

import { isLocalStoreNotAnsweringError, withBootLocalStoreTimeout } from './local-store-timeout';

/** How a question to the storage layer turned out. */
export type ProbeOutcome =
  /** it answered — this part of storage is working */
  | 'answered'
  /** it accepted the question and said nothing at all — the failure this ticket is about */
  | 'silent'
  /** it refused with an error, which is a different (and much more debuggable) fact */
  | 'failed'
  /** the browser does not offer this at all, so it says nothing about anything */
  | 'unavailable';

/** What the probe found, flattened for a telemetry payload (#4401). */
export interface LocalStoreProbeResult {
  /** Whether a brand-new, empty database could be opened at all. */
  local_store_probe: ProbeOutcome;
  /** How long that took, or how long we waited before giving up. */
  local_store_probe_ms: number;
  /** Whether the browser answered when asked how much room is left. */
  local_store_space: ProbeOutcome;
  /** Bytes this origin is allowed, when the browser said. */
  local_store_quota_bytes?: number;
  /** Bytes this origin has used, when the browser said. */
  local_store_used_bytes?: number;
}

/** Everything the probe needs, injected so it can be tested under node (#4401). */
export interface LocalStoreProbeDeps {
  /**
   * Open a throwaway database and resolve when it is open. Null when this browser has no
   * IndexedDB at all — which is itself an answer, just not one about being jammed.
   */
  openThrowaway: (() => Promise<unknown>) | null;
  /** Ask how much storage room is left. Null when the browser does not offer it. */
  estimateSpace: (() => Promise<{ quota?: number, usage?: number }>) | null;
  /** Race a question against a time limit, rejecting recognisably when it goes unanswered. */
  bound: <T>(question: Promise<T>, questionName: string) => Promise<T>;
  /** Milliseconds since some fixed point; only differences are used. */
  now: () => number;
}

/** The name of the throwaway database. It holds nothing and may be abandoned at any time. */
export const LOCAL_STORE_PROBE_DB_NAME = 'cs-storage-probe';

/**
 * Find out whether the browser's storage answers at all (#4401).
 *
 * Never throws and never rejects: it runs while a boot is already failing, and a probe
 * that took the failing path down with it would be worse than no probe. Every outcome,
 * including "the browser does not have this", comes back as a value.
 */
export async function probeLocalStoreAsync(deps: LocalStoreProbeDeps): Promise<LocalStoreProbeResult> {
  const startedAt = deps.now();
  let probe: ProbeOutcome = 'unavailable';
  if (deps.openThrowaway) {
    try {
      await deps.bound(Promise.resolve(deps.openThrowaway()), `probe:open:${LOCAL_STORE_PROBE_DB_NAME}`);
      probe = 'answered';
    } catch (err) {
      probe = isLocalStoreNotAnsweringError(err) ? 'silent' : 'failed';
    }
  }
  const probeMs = Math.round(deps.now() - startedAt);

  let space: ProbeOutcome = 'unavailable';
  let quota: number | undefined = undefined;
  let used: number | undefined = undefined;
  if (deps.estimateSpace) {
    try {
      // this can go silent too when the storage layer is the thing that is wedged, which
      // is exactly why it is bounded rather than simply awaited
      const estimate = await deps.bound(Promise.resolve(deps.estimateSpace()), 'probe:storage.estimate');
      space = 'answered';
      quota = estimate?.quota;
      used = estimate?.usage;
    } catch (err) {
      space = isLocalStoreNotAnsweringError(err) ? 'silent' : 'failed';
    }
  }

  const result: LocalStoreProbeResult = {
    local_store_probe: probe,
    local_store_probe_ms: probeMs,
    local_store_space: space,
  };
  // only report the numbers when the browser actually gave them — a zero would read as
  // "no room left", which is a completely different fact from "it did not say"
  if (quota != null) result.local_store_quota_bytes = quota;
  if (used != null) result.local_store_used_bytes = used;
  return result;
}

/**
 * What the probe says about where a rescue should be aimed (#4401).
 *
 * Deliberately three answers, not two. "I could not tell" is a real outcome — the
 * browser may not offer IndexedDB at all, or the probe may have failed with an error
 * rather than going silent — and treating it as either of the other two would send the
 * user to a remedy chosen on a guess.
 */
export type WedgeScope =
  /** only the app's own database is stuck; a store opened under a different name should work */
  | 'this-database'
  /** the whole origin's storage is stuck; a different name is queued behind the same wedge */
  | 'all-storage'
  /** not enough evidence to say */
  | 'unknown';

/**
 * Read the probe's outcome as advice about which rescue can possibly work (#4401).
 *
 * `probeSpeaksForTheLocalStore` is not optional politeness — it is what stops the answer
 * being applied to a store the probe never touched. The probe opens an INDEXEDDB
 * database, and on the iOS native app the local store is SQLite instead, so there the
 * probe can answer a cheerful "storage works" about a completely different storage
 * system while the one that is jammed says nothing at all. Acting on that would offer the
 * user a reset that cannot help and costs them their unsynced data.
 */
export function scopeOfWedge(result: LocalStoreProbeResult, probeSpeaksForTheLocalStore: boolean): WedgeScope {
  if (!probeSpeaksForTheLocalStore) return 'unknown';
  if (result.local_store_probe === 'answered') return 'this-database';
  if (result.local_store_probe === 'silent') return 'all-storage';
  return 'unknown';
}

/**
 * Whether the probe found the storage layer itself misbehaving (df-b83a2b04a9c2).
 *
 * This picks the report's severity tier. A question that went silent or failed is an
 * unresolved storage problem, so it is reported as a PROBLEM. An answer, or a browser
 * that does not offer the question at all, means nothing is wrong with storage. That
 * reading is an observation and goes out as INFO. The stuck start that triggered the
 * probe is already reported separately as `SplashStuckOnBoot`, so filing a healthy
 * reading as a second PROBLEM only doubled the count. See design-docs/telemetry.md,
 * "Three telemetry severity tiers".
 */
export function probeFoundAProblem(result: LocalStoreProbeResult): boolean {
  const misbehaved = (outcome: ProbeOutcome) => outcome === 'silent' || outcome === 'failed';
  return misbehaved(result.local_store_probe) || misbehaved(result.local_store_space);
}


/**
 * The real browser-backed probe (#4401).
 *
 * The globals are read INSIDE this function rather than at the top of the module, so the
 * module still loads under plain node where neither exists.
 *
 * The throwaway database is opened and then left alone. It is never deleted here on
 * purpose: `indexedDB.deleteDatabase` waits for every open connection to close and can
 * therefore hang exactly like the thing being diagnosed, so a cleanup would risk
 * reintroducing the failure inside the code that exists to measure it. What is left
 * behind is one empty database with no object stores.
 */
export function makeBrowserProbeDeps(): LocalStoreProbeDeps {
  const idb: IDBFactory | undefined = typeof indexedDB !== 'undefined' ? indexedDB : undefined;
  const storage: StorageManager | undefined =
    typeof navigator !== 'undefined' && navigator.storage && typeof navigator.storage.estimate === 'function'
      ? navigator.storage : undefined;
  return {
    openThrowaway: idb ? () => new Promise<unknown>((resolve, reject) => {
      // no version is passed, so an existing throwaway database opens at whatever
      // version it already has and no upgrade is ever requested — an upgrade is the one
      // thing that can be BLOCKED by another connection, which would make this probe
      // answer "silent" for a reason that has nothing to do with the failure
      const request = idb.open(LOCAL_STORE_PROBE_DB_NAME);
      request.onsuccess = () => {
        try { request.result.close(); } catch { /* nothing to do; the probe already answered */ }
        resolve(true);
      };
      request.onerror = () => reject(request.error ?? new Error('probe open failed'));
      // a blocked open is not silence — it is a different, nameable condition, and
      // saying so is more useful than waiting for the time limit to call it silence
      request.onblocked = () => reject(new Error('probe open was blocked by another connection'));
    }) : null,
    estimateSpace: storage ? () => storage.estimate() as Promise<{ quota?: number, usage?: number }> : null,
    bound: withBootLocalStoreTimeout,
    now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
  };
}
